import { useToast } from "@chakra-ui/toast";
import { FormikConfig, FormikHelpers } from "formik";
import { FC, useCallback } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";

import {
  UrlMappingDestinationModeType,
  UrlMappingMatchType,
} from "~/src/__generated__/schema";
import { UrlMappingEditFormContainer_Mutation } from "~/src/__relay_artifacts__/UrlMappingEditFormContainer_Mutation.graphql";
import { UrlMappingEditFormContainer_Query } from "~/src/__relay_artifacts__/UrlMappingEditFormContainer_Query.graphql";
import { useFormErrorHandler } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";

import { UrlMappingForm, UrlMappingFormValues } from "./presentations";
import {
  buildUrlMappingMutationInput,
  conditionRowsFromJson,
  sourceUrlsToText,
} from "./urlMappingFormUtils";

type Props = {
  siteSlug: string;
  urlMappingId: string;
};

const query = graphql`
  query UrlMappingEditFormContainer_Query($urlMappingId: ID!) {
    node(id: $urlMappingId) {
      ... on UrlMapping {
        id
        name
        active
        matchType
        priority
        sourceUrl
        sourceHost
        sourcePath
        regexPattern
        destinationUrl
        destinationMode
        conditions
        startsAt
        endsAt
      }
    }
  }
`;

const mutation = graphql`
  mutation UrlMappingEditFormContainer_Mutation(
    $input: UpdateUrlMappingInput!
  ) {
    updateUrlMapping(input: $input) {
      urlMapping {
        id
        name
        active
        matchType
        priority
        sourceUrl
        sourceHost
        sourcePath
        regexPattern
        destinationUrl
        destinationMode
        conditions
        startsAt
        endsAt
      }
    }
  }
`;

export const UrlMappingEditFormContainer: FC<Props> = ({
  siteSlug,
  urlMappingId,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { onFormError } = useFormErrorHandler();

  const { node } = useLazyLoadQuery<UrlMappingEditFormContainer_Query>(query, {
    urlMappingId,
  });

  const updateMutation =
    useMutationCommit<UrlMappingEditFormContainer_Mutation>(mutation);

  if (!node?.id) {
    throw new Error("URL mapping not found");
  }

  const conditionsJson =
    (node.conditions as Record<string, unknown> | null | undefined) ?? {};

  const initialValues: UrlMappingFormValues = {
    name: node.name || "",
    active: node.active ?? true,
    matchType: node.matchType ?? "EXACT",
    priority: node.priority ?? 100,
    sourceUrl: node.sourceUrl || "",
    sourceUrls: sourceUrlsToText(node.sourceUrl, conditionsJson),
    sourceHost: node.sourceHost || "",
    sourcePath: node.sourcePath || "",
    regexPattern: node.regexPattern || "",
    destinationUrl: node.destinationUrl ?? "",
    destinationMode:
      node.destinationMode === "TEMPLATE" ? "TEMPLATE" : "STATIC",
    conditionRows: conditionRowsFromJson(conditionsJson),
    startsAt: node.startsAt ? new Date(node.startsAt) : null,
    endsAt: node.endsAt ? new Date(node.endsAt) : null,
  };

  const handleSubmit = useCallback<
    FormikConfig<UrlMappingFormValues>["onSubmit"]
  >(
    async (
      values: UrlMappingFormValues,
      { setErrors }: FormikHelpers<UrlMappingFormValues>
    ) => {
      try {
        const input = buildUrlMappingMutationInput(values);
        const { updateUrlMapping } = await updateMutation({
          variables: {
            input: {
              urlMappingId: node.id as string,
              ...input,
              matchType: input.matchType as UrlMappingMatchType,
              destinationMode:
                input.destinationMode as UrlMappingDestinationModeType,
            },
          },
        });
        if (!updateUrlMapping?.urlMapping) throw new Error("assertion failed");

        toast({ title: "URLマッピングを更新しました", status: "success" });
        navigate(`/sites/${siteSlug}/settings?tab=url_mappings`, {
          state: { refetchUrlMappingList: true },
        });
      } catch (err) {
        onFormError(err as any, setErrors);
      }
    },
    [navigate, node.id, onFormError, siteSlug, toast, updateMutation]
  );

  const handleCancel = useCallback(() => {
    navigate(`/sites/${siteSlug}/settings?tab=url_mappings`);
  }, [navigate, siteSlug]);

  return (
    <UrlMappingForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};
